'use client';

import ReactSlick from '@fe-common-ui/Slick';
import { styled } from '@linaria/react';
import { useCallback, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import DotsWrapper from '@fe-news/components/IndexMobileWrapper/NavigatorCarousel/DotsWrapper';
import { SCREEN_SIZE } from '@fe-common-utils/constants/screen';
import PopTopicCard from '@fe-news/components/molecules/PopTopicCard';
import { GTM_EVENT_POP_TOPIC_CAROUSEL, PopTopicListData } from '@fe-news/constants/pop-topic/type';
import { GTMEventKey, sendGTM } from '@fe-cnyes/fe-common-gtm';

const StyledSlider = styled.div`
  min-height: 687px;
  position: relative;
  background-color: #fff;
  padding-bottom: 27px;
  img {
    border-radius: 10px;
  }

  .slick-slide {
    height: 660px;
    padding-top: 15px;

    @media screen and (max-width: ${SCREEN_SIZE.TABLET}px) {
      height: 690px;
    }
  }

  .slick-arrow {
    display: none !important;
  }
`;

const ArrowTouch = styled.button`
  position: absolute;
  top: 0;
  height: 100%;
  background-color: transparent;
  z-index: 2;
`;

type PopTopicCarouselProps = {
  data: PopTopicListData[];
  isError?: boolean;
};

const PopTopicCarousel = ({ data, isError }: PopTopicCarouselProps) => {
  const [scrollIndex, setScrollIndex] = useState(0);
  const [speed, setSpeed] = useState(1000);
  const sliderReference = useRef<null | {
    slickNext: () => void;
    slickPrev: () => void;
  }>(null);

  const alreadyRunIndex = new Set([0]);

  const handleAfterChange = (current: number) => {
    if (alreadyRunIndex.has(current)) return setSpeed(3000);
    alreadyRunIndex.add(current);
  };

  const settings = useMemo(
    () => ({
      dots: false,
      infinite: true,
      slidesToShow: 1,
      speed: 500,
      autoplay: true,
      autoplaySpeed: 2000,
      slidesToScroll: 1,
      beforeChange: (_: number, newIndex: number) => {
        setScrollIndex(newIndex);
      },
      afterChange: handleAfterChange
    }),
    [speed]
  );

  const onForceSlide = useCallback(
    (back = false) =>
      () => {
        if (sliderReference.current) {
          const item = back ? '置頂時事_下一頁' : '置頂時事_上一頁';

          if (back) {
            sliderReference.current.slickNext();
            sendGTM({
              ...GTM_EVENT_POP_TOPIC_CAROUSEL,
              [GTMEventKey.CLICK_ITEM]: `${item}`
            });
          } else {
            sliderReference.current.slickPrev();
            sendGTM({
              ...GTM_EVENT_POP_TOPIC_CAROUSEL,
              [GTMEventKey.CLICK_ITEM]: `${item}`
            });
          }
        }
      },
    []
  );

  const isEmpty = !data || data.length === 0;

  if (isError) {
    return <div>Error....</div>;
  }

  return (
    !isEmpty && (
      <StyledSlider>
        <ArrowTouch style={{ left: -11 }} onClick={onForceSlide()} id='left-arrow-touch'>
          <Image src='/assets/icons/symbol-icon-prev.svg' alt='arrow back' width={36} height={36} unoptimized />
        </ArrowTouch>
        <ReactSlick ref={sliderReference} {...settings}>
          {data.map(issue => {
            return <PopTopicCard key={issue.id} data={issue} isCarousel />;
          })}
        </ReactSlick>
        <DotsWrapper newsNumber={data.length} scrollIndex={scrollIndex} />
        <ArrowTouch style={{ right: -10 }} onClick={onForceSlide(true)} id='right-arrow-touch'>
          <Image src='/assets/icons/symbol-icon-next.svg' alt='arrow back' width={36} height={36} unoptimized />
        </ArrowTouch>
      </StyledSlider>
    )
  );
};

export default PopTopicCarousel;
